import { useEffect, useRef } from 'react';

export default function BrandStatementSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.querySelectorAll('.brand-animate').forEach((child, i) => {
              (child as HTMLElement).style.animationDelay = `${i * 0.2}s`;
              child.classList.add('animate-fade-in-up');
            });
            obs.unobserve(el);
          }
        });
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} id="about" className="relative w-full py-24 md:py-32 bg-background-950 overflow-hidden">
      {/* Soft spotlight */}
      <div className="absolute top-[-200px] left-1/2 -translate-x-1/2 w-[900px] h-[900px] bg-primary-500/10 blur-3xl" />

      <div className="relative w-full px-6 md:px-10 lg:px-16">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center">
            <p className="brand-animate opacity-0 text-primary-500 text-xs font-bold tracking-[0.3em] uppercase mb-5">
              About Us
            </p>
            <h2 className="brand-animate opacity-0 text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-foreground-50 tracking-tight leading-[0.95]">
              Gift. Drink. Connect.
            </h2>
            <p className="brand-animate opacity-0 mt-6 mx-auto max-w-3xl text-foreground-400 text-base md:text-lg leading-relaxed">
              Welcome to MARKER - the mobile app that redefines how you connect with friends over drinks, no matter the distance.
            </p>
          </div>

          {/* Body cards */}
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
            <div className="brand-animate opacity-0 md:pr-1 md:flex">
              <div className="flex flex-col justify-between w-full p-6 rounded-2xl bg-background-100/10 border border-background-200/20">
                <div>
                  <h3 className="text-foreground-50 text-lg font-bold mb-3 flex items-center gap-2">
                    <i className="ri-gift-line text-primary-500" /> Simple gestures, timeless joy
                  </h3>
                  <p className="text-foreground-400 text-sm md:text-[15px] leading-relaxed">
                    We believe great memories start with simple gestures—and there&apos;s nothing more timeless than buying a friend a drink.
                    Whether it&apos;s a birthday, thanks, or just because, MARKER makes it easy to send a drink that lands exactly when it matters.
                  </p>
                </div>
              </div>
            </div>

            <div className="brand-animate opacity-0 md:flex">
              <div className="flex flex-col justify-between w-full p-6 rounded-2xl bg-background-100/10 border border-background-200/20">
                <div>
                  <h3 className="text-foreground-50 text-lg font-bold mb-3 flex items-center gap-2">
                    <i className="ri-map-pin-line text-primary-500" /> Redeem anywhere, anytime
                  </h3>
                  <p className="text-foreground-400 text-sm md:text-[15px] leading-relaxed">
                    With MARKER, you can purchase bar drinks for friends and have them redeemed at participating venues worldwide.
                    Choose from curated cocktails, craft beers, or local favorites—and add a personal message to make it meaningful.
                  </p>
                </div>
              </div>
            </div>

            <div className="brand-animate opacity-0 md:pl-1 md:flex">
              <div className="flex flex-col justify-between w-full p-6 rounded-2xl bg-background-100/10 border border-background-200/20">
                <div>
                  <h3 className="text-foreground-50 text-lg font-bold mb-3 flex items-center gap-2">
                    <i className="ri-heart-pulse-line text-primary-500" /> Connection that feels effortless
                  </h3>
                  <p className="text-foreground-400 text-sm md:text-[15px] leading-relaxed">
                    We&apos;re here to make connection effortless, spontaneous, and fun. Think of MARKER as a modern-day bar tab with global reach—
                    powered by friendship, celebration, and a touch of surprise.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Closing CTA */}
          <div className="brand-animate opacity-0 mt-10 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between text-center sm:text-left">
            <p className="text-foreground-400 text-sm md:text-[15px] leading-relaxed">
              Cheers to better ways of staying close. Cheers to MARKER.
              <br />
              <span className="text-primary-500 font-bold">Gift. Drink. Connect.</span>
            </p>
            <a
              href="#download"
              className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-primary-500 text-background-950 text-sm font-bold hover:bg-primary-400 transition-colors"
            >
              Get MARKER
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}